import { Directive, OnInit, Input, Output, EventEmitter, ChangeDetectorRef, Injector, HostBinding } from '@angular/core';
import { ControlValueAccessor, NgControl, UntypedFormControl, FormGroupDirective, NgForm } from '@angular/forms';
import { TranslateService } from '@ngx-translate/core';
import { ErrorStateMatcher } from '@angular/material/core';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { InputValidator, IValidationMessagesItemMap } from '@nimbus-library/features/shared/components/material/input-validator';
import { Icon, ICONS } from '@nimbus-library/features/shared/resources/icons';
import { INimbusFormGroup } from '@nimbus-library/features/shared/angular/forms';
import { MatIconOverlay } from '@nimbus-library/features/shared/components/material/mat-icon-overlay/mat-icon-overlay.model';
import { NimbusCdkArrowCssClass } from '../../services/tooltip/nimbusTooltip.service';

@Directive()
export abstract class IInputComponent<T = string> implements OnInit, ControlValueAccessor, ErrorStateMatcher {
  @Input() public validationMessages: IValidationMessagesItemMap = {
    cannotValidate: (name, validationDescriptor, translateService) =>
      translateService.instant(TranslationKeys.SettingsFormEmergencyDestinationCannotValidate),
    destinationInvalid: (name, validationDescriptor, translateService) =>
      translateService.instant(TranslationKeys.SettingsFormEmergencyDestionationNotValid),
  };
  @Input() public teamStatusIcon: string;
  @Input() public label: string;
  @Input() public placeholder: string;
  @Input() public maxLength: number;
  @Input() public isWarning: boolean = false;
  @Input() public warningMessage: string;
  @Input() public warningMessageAdditionalInfo: string;
  @Input() public isErrorMessage: boolean;
  @Input() public errorMessage: string;
  @Input() public infoMessageStyle: 'info' | 'success' | 'error' | 'warning' = 'info';
  @Input() public infoMessageTextStyle: 'info' | 'success' | 'error' | 'warning' = 'info';
  @Input() public isOverlayWarningMessage: boolean;
  @Input() public isOverlayErrorMessage: boolean;
  @Input() public overlayErrorMessagePosition: NimbusCdkArrowCssClass = 'top-center';
  @Input() public overlayMessage: MatIconOverlay;
  @Input() public overlayMessagePosition: NimbusCdkArrowCssClass = 'top-center';

  @Output() public stateChange = new EventEmitter<string>();

  protected innerDisabled: boolean = false;

  public inputValidator: InputValidator;
  public ngControl: NgControl;
  public formGroup: INimbusFormGroup<any>;
  public innerValue: T;
  public isRequired: boolean = false;
  public TranslationKeys = TranslationKeys;
  public icons = ICONS;

  @HostBinding('class.disabled')
  @Input()
  public get isDisabled(): boolean {
    return this.innerDisabled;
  }

  public set isDisabled(value: boolean) {
    this.innerDisabled = value;
  }

  @Input()
  public get value(): T {
    return this.innerValue;
  }

  public set value(value: T) {
    if (value !== this.innerValue) {
      this.innerValue = value;
      this.propagateChange(value);
    }
  }

  @Input()
  public get required(): boolean {
    return this.isRequired;
  }

  public set required(value: boolean) {
    this.isRequired = value;
    this.stateChange.emit();
    this.changeDetectorRef.markForCheck();
  }

  public propagateChange: any = () => {};

  public propagateTouched: any = () => {};

  public get isRequiredControl(): boolean {
    return this.ngControl ? this.ngControl.valueAccessor['required'] : false;
  }

  public get messageIcon(): Icon {
    switch (this.infoMessageStyle) {
      case 'warning':
        return ICONS.EXCLAMATION_TRIANGLE;
      case 'error':
        return ICONS.EXCLAMATION_TRIANGLE;
      default:
        return ICONS.INFO_CIRCLE;
    }
  }

  public constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly ngInjector: Injector,
    protected readonly translateServ: TranslateService,
  ) {}

  public ngOnInit(): void {
    this.inputValidator = new InputValidator(this.validationMessages, this.translateServ);
    try {
      this.ngControl = this.ngInjector.get(NgControl, null, { self: true, optional: true });
      this.ngControl.valueAccessor = this;
    } catch (e) {}
  }

  public isErrorState(control?: UntypedFormControl | null, form?: FormGroupDirective | NgForm | null): boolean {
    // Limiting the scope of the injector to make sure we always get the right ngControl see: https://dev.azure.com/luware/Nimbus/_workitems/edit/98708
    return this.ngControl && this.ngControl.invalid && (this.ngControl.dirty || this.ngControl.touched);
  }

  public writeValue(value: T): void {
    if (value !== this.innerValue) {
      this.innerValue = value;
    }
    this.markForCheck();
  }

  public registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  public registerOnTouched(fn: any): void {
    this.propagateTouched = fn;
  }

  public setDisabledState(isDisabled: boolean): void {
    this.isDisabled = isDisabled;
    this.markForCheck();
  }

  public onClearValueClick(event: Event): void {
    event.stopPropagation();
    this.value = null;
  }

  public markForCheck(): void {
    this.changeDetectorRef.markForCheck();
  }
}
